import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload as UploadIcon, FileText, Settings, Key } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useConfirm } from '../contexts/ConfirmContext';
import type {
  FileUpload as FileUploadType,
  PromptTemplate,
  GlobalPromptTemplate,
  CostEstimation,
  StartProcessingRequest,
  ApiKeyStatus,
  ColumnAnalysisResult,
  PaginatedResponse,
} from '../types';
import { theme } from '../styles/theme';
import { authAPI, promptsAPI, globalPromptsAPI, jobsAPI, filesAPI } from '../services/api';
import axios from 'axios';
import { extractApiError } from '../utils/errorMessages';
import UploadTab from '../components/UploadTab';
import PromptConfigTab from '../components/PromptConfigTab';
import ProcessingConfigTab from '../components/ProcessingConfigTab';

const CreateJob: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const confirm = useConfirm();
  // Stable per-attempt idempotency key — persists across React re-renders so a
  // duplicate network retry sends the same key and the server returns the
  // existing job instead of creating a second one.  Reset only after success.
  const idempotencyKeyRef = React.useRef<string>(crypto.randomUUID());
  const [activeTab, setActiveTab] = useState<'upload' | 'prompt' | 'processing'>('upload');
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [error, setError] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<FileUploadType[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<FileUploadType | null>(null);
  
  // Prompt configuration state
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [globalTemplates, setGlobalTemplates] = useState<GlobalPromptTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<PromptTemplate | GlobalPromptTemplate | null>(null);
  const [prompt, setPrompt] = useState('');
  const [projectName, setProjectName] = useState('');
  const [promptName, setPromptName] = useState('');
  const [promptDescription, setPromptDescription] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  
  // Processing configuration state
  // Decision: PDR-002 follow-up — no hardcoded default here (the old
  // 'gemini-2.0-flash' predates the live proxy catalogue and is not in the
  // real model list). ModelSelect's self-correcting effect picks
  // models[0] once the live list loads; the empty string is a safe initial
  // value because handleEstimateForJob is button-triggered, not fired on mount.
  const [modelName, setModelName] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [chunkSize, setChunkSize] = useState(20);
  const [maxConcurrent, setMaxConcurrent] = useState(5);
  const [outputMultiplier, setOutputMultiplier] = useState(1.8);
  const [estimation, setEstimation] = useState<CostEstimation | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [estimatedConfig, setEstimatedConfig] = useState<StartProcessingRequest | null>(null);
  
  // File selection for processing
  const [selectedFile, setSelectedFile] = useState<FileUploadType | null>(null);
  const [columnAnalysis, setColumnAnalysis] = useState<ColumnAnalysisResult | null>(null);
  const [analyzingColumns, setAnalyzingColumns] = useState(false);
  const [selectedJoinColumn, setSelectedJoinColumn] = useState<string>('');
  const [showJoinColumnDialog, setShowJoinColumnDialog] = useState(false);
  const [apiStatus, setApiStatus] = useState<ApiKeyStatus | null>(null);

  // Load data on component mount
  React.useEffect(() => {
    loadTemplates();
    loadGlobalTemplates();
    loadApiStatus();
  }, []);

  // Auto-select first uploaded file when switching to prompt tab
  React.useEffect(() => {
    if (activeTab === 'prompt' && uploadedFiles.length > 0 && !selectedFile) {
      setSelectedFile(uploadedFiles[0]);
    }
  }, [activeTab, uploadedFiles, selectedFile]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const newFiles = Array.from(e.dataTransfer.files);
      setFiles([...files, ...newFiles]);
      setError('');
      // Auto-upload immediately
      await handleUploadFiles(newFiles);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const newFiles = Array.from(e.target.files);
      setFiles([...files, ...newFiles]);
      setError('');
      // Auto-upload immediately
      await handleUploadFiles(newFiles);
    }
  };

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
    setError('');
  };

  const handleUploadFiles = async (filesToUpload: File[]) => {
    if (filesToUpload.length === 0) {
      setError('Please select at least one file');
      return;
    }

    setUploading(true);
    setError('');
    
    try {
      // Import the API service
      const { filesAPI } = await import('../services/api');
      
      // Upload files one by one and collect results
      const uploadResults: FileUploadType[] = [];
      const uploadPromises = filesToUpload.map(async (file) => {
        try {
          // Upload with progress tracking
          const response = await filesAPI.upload(file, (progressEvent: any) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(prev => ({ ...prev, [file.name]: percentCompleted }));
          });
          
          uploadResults.push(response.data);
          return { success: true, file: file.name, data: response.data };
        } catch (err: unknown) {
          return { 
            success: false, 
            file: file.name, 
            error: extractApiError(err, 'Upload failed'),
          };
        }
      });

      const results = await Promise.all(uploadPromises);
      
      // Check if any uploads failed
      const failed = results.filter(r => !r.success);
      
      if (failed.length > 0) {
        const errorMsg = failed.length === filesToUpload.length 
          ? 'All files failed to upload' 
          : `${failed.length} file(s) failed to upload: ${failed.map(f => f.file).join(', ')}`;
        setError(errorMsg);
        setUploading(false);
      } else {
        // All successful - store uploaded files and show preview
        setUploadedFiles(uploadResults);
        setUploading(false);
        
        // If single file, show preview automatically
        if (uploadResults.length === 1) {
          setPreviewData(uploadResults[0]);
          setShowPreview(true);
        }
      }

    } catch (err: unknown) {
      setError('Failed to upload files. Please try again.');
      setUploading(false);
    }
  };

  const handleViewPreview = async (fileId: string) => {
    try {
      const { filesAPI } = await import('../services/api');
      const response = await filesAPI.preview(fileId);
      
      // Find the uploaded file
      const file = uploadedFiles.find(f => f.id === fileId);
      if (file) {
        setPreviewData({
          ...file,
          preview_data: response.data.preview
        });
        setShowPreview(true);
      }
    } catch (err: unknown) {
      setError('Failed to load preview');
    }
  };

  const handleContinue = () => {
    navigate('/jobs');
  };

  const handleUploadMore = () => {
    setFiles([]);
    setUploadedFiles([]);
    setUploadProgress({});
    setShowPreview(false);
    setPreviewData(null);
  };

  // Load API status
  const loadApiStatus = async () => {
    try {
      const response = await authAPI.getApiKeyStatus();
      setApiStatus(response.data);
    } catch (error) {
      console.error('Failed to load API status:', error);
    }
  };

  // Load user prompt templates
  const loadTemplates = async () => {
    try {
      const response = await promptsAPI.list();
      const templateData: PromptTemplate[] = Array.isArray(response.data) 
        ? response.data 
        : (response.data as PaginatedResponse<PromptTemplate>).results || [];
      setTemplates(templateData);
      
      const defaultTemplate = templateData.find((t) => t.is_default);
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate);
        setPrompt(defaultTemplate.prompt_text);
      }
    } catch (error: unknown) {
      console.error('Failed to load templates:', error);
      setTemplates([]);
      if (axios.isAxiosError(error) && error.response?.status !== 404) {
        console.warn('Could not load templates. You can still create jobs without templates.');
      }
    }
  };

  // Load global prompt templates
  const loadGlobalTemplates = async () => {
    try {
      const response = await globalPromptsAPI.list();
      const templateData: GlobalPromptTemplate[] = Array.isArray(response.data) 
        ? response.data 
        : (response.data as PaginatedResponse<GlobalPromptTemplate>).results || [];
      setGlobalTemplates(templateData);
    } catch (error) {
      console.error('Failed to load global templates:', error);
      setGlobalTemplates([]);
    }
  };

  // Save template
  const handleSaveTemplate = async () => {
    if (!promptName || !prompt) return;
    
    try {
      await promptsAPI.create({
        name: promptName,
        description: promptDescription,
        prompt_text: prompt,
      });
      
      await loadTemplates();
      setShowSaveTemplate(false);
      setPromptName('');
      setPromptDescription('');
    } catch (error: unknown) {
      toast({ title: extractApiError(error, "Couldn't save that template"), variant: 'error' });
    }
  };

  // Select template
  const handleSelectTemplate = (template: any) => {
    setSelectedTemplate(template);
    setPrompt(template.prompt_text);
  };

  // Check if configuration has changed since last estimation
  const hasConfigChanged = (): boolean => {
    if (!estimatedConfig) return false;
    
    return (
      estimatedConfig.file_id !== selectedFile?.id ||
      estimatedConfig.system_instructions !== prompt ||
      estimatedConfig.model_name !== modelName ||
      estimatedConfig.output_multiplier !== outputMultiplier
    );
  };

  // Analyze columns for join column suggestion
  const handleAnalyzeColumns = async () => {
    if (!selectedFile) {
      toast({ title: 'Choose a file first', variant: 'warning' });
      return;
    }
    
    setAnalyzingColumns(true);
    try {
      const response = await jobsAPI.analyzeColumns({
        file_id: selectedFile.id as string,
      });
      
      setColumnAnalysis(response.data);
      setSelectedJoinColumn(response.data.suggested_join_column);
      setShowJoinColumnDialog(true);
    } catch (error: unknown) {
      console.error('Failed to analyze columns:', error);
      toast({ title: "Couldn't read the columns in that file", description: 'Check the file opens correctly, then try again.', variant: 'error' });
    } finally {
      setAnalyzingColumns(false);
    }
  };

  // Estimate cost using Gemini token counter
  const handleEstimateForJob = async () => {
    if (!selectedFile || !prompt) {
      toast({ title: 'Choose a file and write a prompt first', variant: 'warning' });
      return;
    }
    
    setEstimating(true);
    try {
      const response = await jobsAPI.estimate({
        file_id: selectedFile.id,
        prompt_text: prompt,
        model_name: modelName,
        temperature: temperature,
        chunk_size: chunkSize,
        max_concurrent: maxConcurrent,
        output_multiplier: outputMultiplier,
      });
      
      setEstimation(response.data);
      
      setEstimatedConfig({
        file_id: selectedFile.id,
        system_instructions: prompt,
        model_name: modelName,
        chunk_size: chunkSize,
        max_concurrent: maxConcurrent,
      });
    } catch (error: unknown) {
      console.error('Failed to estimate:', error);
      
      if (axios.isAxiosError(error) && error.response?.data?.error === 'API_CREDENTIALS_NOT_CONFIGURED') {
        toast({
          title: 'API Credentials Not Configured',
          description: (error.response.data as Record<string, unknown>).detail as string || 'Please configure your Gemini or Vertex AI credentials.',
          variant: 'error',
          duration: 5000,
        });
        navigate('/profile?tab=api');
      } else {
        toast({ title: "Couldn't estimate the cost", description: 'Try again in a moment.', variant: 'error' });
      }
    } finally {
      setEstimating(false);
    }
  };

  // Start job with estimation
  const handleStartJob = async () => {
    if (!selectedFile || !prompt) {
      toast({ title: 'Choose a file and write a prompt first', variant: 'warning' });
      return;
    }
    
    if (!estimation || !estimation.estimated_cost) {
      toast({ title: 'Estimate the cost before starting', variant: 'warning' });
      return;
    }
    
    if (hasConfigChanged()) {
      toast({
        title: 'Your settings changed since the last estimate',
        description: 'Re-estimate the cost before starting this run.',
        variant: 'warning',
      });
      return;
    }
    
    if (!columnAnalysis || !selectedJoinColumn) {
      await handleAnalyzeColumns();
      return;
    }
    
    await proceedWithJobStart();
  };

  // Proceed with job start after join column is confirmed
  const proceedWithJobStart = async () => {
    if (!selectedFile || !prompt || !estimation) return;
    
    // See the matching note in Jobs.tsx: the paid branch used `\\n` inside a
    // template literal and printed the characters "\n" in the dialog.
    const rowLabel = (estimation.row_count || 0).toLocaleString();
    const modelLabel = estimation.model_name || 'the selected model';
    const costMessage = estimation.is_free
      ? {
          title: 'Start this run?',
          description: `${rowLabel} rows with ${modelLabel}. No cost on the free tier.`,
          confirmText: 'Start run',
        }
      : {
          title: 'Start this run?',
          description:
            `${rowLabel} rows with ${modelLabel}. Estimated cost ` +
            `$${(estimation.estimated_cost?.usd || 0).toFixed(4)} USD ` +
            `(S$${(estimation.estimated_cost?.sgd || 0).toFixed(4)}).`,
          confirmText: 'Start run',
        };
    
    if (!(await confirm(costMessage))) {
      return;
    }
    
    try {
      const response = await jobsAPI.start({
        file_id: selectedFile.id,
        project_name: projectName || undefined,
        prompt_text: prompt,
        prompt_template_id: selectedTemplate?.id,
        model_name: modelName,
        temperature: temperature,
        chunk_size: chunkSize,
        max_concurrent: maxConcurrent,
        join_column: selectedJoinColumn || undefined,
      }, idempotencyKeyRef.current);
      // Rotate key only after a confirmed new job so any retry still uses the same key.
      idempotencyKeyRef.current = crypto.randomUUID();
      if (response.data.is_replay) {
        toast({
          title: 'Job already submitted',
          description: 'This job was already submitted — showing your existing job.',
          variant: 'warning',
          duration: 5000,
        });
      } else {
        toast({ title: 'Run started', variant: 'success' });
      }
      navigate('/jobs');
    } catch (error: unknown) {
      console.error('Failed to start job:', error);
      
      if (axios.isAxiosError(error) && error.response?.data?.error === 'API_CREDENTIALS_NOT_CONFIGURED') {
        toast({
          title: 'API Credentials Not Configured',
          description: (error.response.data as Record<string, unknown>).detail as string || 'Please configure your LiteLLM API key.',
          variant: 'error',
          duration: 5000,
        });
        navigate('/profile?tab=api');
        return;
      }
      
      toast({ title: extractApiError(error, "Couldn't start that run"), variant: 'error' });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handlePreview = (file: FileUploadType) => {
    handleViewPreview(file.id);
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h2 className={`text-3xl font-bold ${theme.text.primary}`}>
            Create New Job
          </h2>
        </div>
        <p className={`${theme.text.secondary} mt-2`}>
          Upload files, configure AI prompts, and set processing parameters
        </p>
      </div>

      {/* Tabs */}
      <div className={`border-b ${theme.border.primary} mb-6`}>
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('upload')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'upload'
                ? 'border-interactive-primary text-interactive-primary'
                : 'border-transparent text-text-tertiary hover:text-text-secondary'
            }`}
          >
            <div className="flex items-center">
              <UploadIcon className="w-4 h-4 mr-2" />
              1. Upload Files
            </div>
          </button>
          <button
            onClick={() => setActiveTab('prompt')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'prompt'
                ? 'border-interactive-primary text-interactive-primary'
                : 'border-transparent text-text-tertiary hover:text-text-secondary'
            }`}
          >
            <div className="flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              2. AI Prompt Config
            </div>
          </button>
          <button
            onClick={() => setActiveTab('processing')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'processing'
                ? 'border-interactive-primary text-interactive-primary'
                : 'border-transparent text-text-tertiary hover:text-text-secondary'
            }`}
          >
            <div className="flex items-center">
              <Settings className="w-4 h-4 mr-2" />
              3. Processing Config
            </div>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'upload' && (
        <UploadTab
          files={files}
          setFiles={setFiles}
          uploading={uploading}
          uploadProgress={uploadProgress}
          error={error}
          setError={setError}
          uploadedFiles={uploadedFiles}
          dragActive={dragActive}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onFileSelect={handleFileChange}
          onRemoveFile={removeFile}
          onUploadMore={handleUploadMore}
          onPreview={handlePreview}
          onNextStep={() => setActiveTab('prompt')}
          formatFileSize={formatFileSize}
        />
      )}

      {/* Prompt Config Tab */}
      {activeTab === 'prompt' && (
        <PromptConfigTab
          uploadedFiles={uploadedFiles}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          templates={templates}
          globalTemplates={globalTemplates}
          selectedTemplate={selectedTemplate}
          handleSelectTemplate={handleSelectTemplate}
          setSelectedTemplate={setSelectedTemplate}
          projectName={projectName}
          setProjectName={setProjectName}
          prompt={prompt}
          setPrompt={setPrompt}
          onNextStep={() => setActiveTab('processing')}
        />
      )}

      {/* Processing Config Tab */}
      {activeTab === 'processing' && (
        <ProcessingConfigTab
          selectedFile={selectedFile}
          prompt={prompt}
          modelName={modelName}
          setModelName={setModelName}
          temperature={temperature}
          setTemperature={setTemperature}
          chunkSize={chunkSize}
          setChunkSize={setChunkSize}
          maxConcurrent={maxConcurrent}
          setMaxConcurrent={setMaxConcurrent}
          outputMultiplier={outputMultiplier}
          setOutputMultiplier={setOutputMultiplier}
          estimation={estimation}
          estimating={estimating}
          estimatedConfig={estimatedConfig as Record<string, unknown> | null}
          hasConfigChanged={hasConfigChanged}
          handleEstimateForJob={handleEstimateForJob}
          handleStartJob={handleStartJob}
          showSaveTemplate={showSaveTemplate}
          setShowSaveTemplate={setShowSaveTemplate}
          promptName={promptName}
          setPromptName={setPromptName}
          promptDescription={promptDescription}
          setPromptDescription={setPromptDescription}
          handleSaveTemplate={handleSaveTemplate}
        />
      )}
    </div>
  );
};

export default CreateJob;
